import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import AnalyticsInitializer from './components/AnalyticsInitializer.jsx';
import SwetrixTracker from './components/SwetrixTracker.jsx';
import { Toaster } from './components/ui/sonner.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
const LeadCaptureWidget = lazy(() => import('./components/LeadCaptureWidget.jsx'));

// Composants chargés immédiatement (critiques pour l'app principale)
const AppShell = lazy(() => import('./components/AppShell.jsx'));

// Lazy loading pour les pages secondaires
const ShareView = lazy(() => import('./components/ShareView.jsx'));
const MentionsLegales = lazy(() => import('./components/MentionsLegales.jsx'));
const CGU = lazy(() => import('./components/CGU.jsx'));
const PricingPage = lazy(() => import('./components/PricingPage.jsx'));
const ContactPage = lazy(() => import('./components/ContactPage.jsx'));
const AboutPage = lazy(() => import('./components/AboutPage.jsx'));
const SubscriptionSuccessPage = lazy(() => import('./components/SubscriptionSuccessPage.jsx'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage.jsx'));
const BlogPage = lazy(() => import('./components/BlogPage.jsx'));
const BlogPostPage = lazy(() => import('./components/BlogPostPage.jsx'));
const SalesPage = lazy(() => import('./components/SalesPage.jsx'));
const CheckoutFunnel = lazy(() => import('./components/CheckoutFunnel.jsx'));
const LandingPage = lazy(() => import('./components/LandingPage.jsx'));
const FAQPage = lazy(() => import('./components/FAQPage.jsx'));
const TutorialsPage = lazy(() => import('./components/TutorialsPage.jsx'));
const LampshadePatternCalculatorPage = lazy(() => import('./components/LampshadePatternCalculatorPage.jsx'));
const LampshadePdfPatternGuidePage = lazy(() => import('./components/LampshadePdfPatternGuidePage.jsx'));
const LampshadeDimensionsGuidePage = lazy(() => import('./components/LampshadeDimensionsGuidePage.jsx'));
const LampshadeShapeGuidePage = lazy(() => import('./components/LampshadeShapeGuidePage.jsx'));
const LampshadeFormulaGuidePage = lazy(() => import('./components/LampshadeFormulaGuidePage.jsx'));
const LampshadeMarginGuidePage = lazy(() => import('./components/LampshadeMarginGuidePage.jsx'));
const DrawLampshadeTemplateGuidePage = lazy(() => import('./components/DrawLampshadeTemplateGuidePage.jsx'));
const PrintableLampshadePatternPage = lazy(() => import('./components/PrintableLampshadePatternPage.jsx'));
const ConicalPatternGuidePage = lazy(() => import('./components/ConicalPatternGuidePage.jsx'));
const EmpirePatternGuidePage = lazy(() => import('./components/EmpirePatternGuidePage.jsx'));
const CylindricalPatternGuidePage = lazy(() => import('./components/CylindricalPatternGuidePage.jsx'));
const DrumPatternGuidePage = lazy(() => import('./components/DrumPatternGuidePage.jsx'));
const OvalPatternGuidePage = lazy(() => import('./components/OvalPatternGuidePage.jsx'));
const RectangularPatternGuidePage = lazy(() => import('./components/RectangularPatternGuidePage.jsx'));
const HexagonalPatternGuidePage = lazy(() => import('./components/HexagonalPatternGuidePage.jsx'));
const CustomLampshadeTemplatePage = lazy(() => import('./components/CustomLampshadeTemplatePage.jsx'));
const PolyphaneLampshadeGuidePage = lazy(() => import('./components/PolyphaneLampshadeGuidePage.jsx'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage.jsx'));
const ErrorTestPage = lazy(() => import('./components/ErrorTestPage.jsx'));
const LoaderTestPage = lazy(() => import('./components/LoaderTestPage.jsx'));
const PhotorealisticTestPage = lazy(() => import('./components/PhotorealisticTestPage.jsx'));
const BabylonTestPage = lazy(() => import('./components/BabylonTestPage.jsx'));
const BabylonUltraPage = lazy(() => import('./components/BabylonUltraPage.jsx'));
const SliderShowcase = lazy(() => import('./components/SliderShowcase.jsx'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard.jsx'));
const AdminRoute = lazy(() => import('./components/admin/AdminRoute.jsx'));

// Composant de chargement artistique avec animation d'abat-jour
function PageLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center overflow-hidden relative">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 rounded-full bg-amber-400/20 animate-ping" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-40 right-20 w-3 h-3 rounded-full bg-orange-400/20 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-2 h-2 rounded-full bg-yellow-400/20 animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-3 h-3 rounded-full bg-amber-300/20 animate-ping" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
      </div>

      <div className="text-center space-y-8 relative z-10">
        {/* Artistic lampshade with pulsing light */}
        <div className="relative mx-auto w-48 h-48 flex items-center justify-center">
          {/* Glowing halo effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-500/30 via-orange-500/20 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '2s' }}></div>

          {/* Main lampshade SVG */}
          <svg className="w-40 h-40 relative z-10" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Ceiling mount */}
            <circle cx="100" cy="20" r="8" fill="#DAA520" opacity="0.8">
              <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
            </circle>
            <rect x="98" y="20" width="4" height="15" fill="#DAA520" opacity="0.7" />

            {/* Hanging cord with subtle swing */}
            <line x1="100" y1="35" x2="100" y2="65" stroke="#C0C0C0" strokeWidth="2" opacity="0.6">
              <animate attributeName="x1" values="100;102;100;98;100" dur="4s" repeatCount="indefinite" />
              <animate attributeName="x2" values="100;102;100;98;100" dur="4s" repeatCount="indefinite" />
            </line>

            {/* Lampshade cone - outer layer */}
            <defs>
              <linearGradient id="fabricGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#F5DEB3" stopOpacity="0.9" />
                <stop offset="50%" stopColor="#DAA520" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#B8860B" stopOpacity="0.8" />
              </linearGradient>
              <filter id="fabricTexture">
                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" result="noise" />
                <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" xChannelSelector="R" yChannelSelector="G" />
              </filter>
              <radialGradient id="innerGlow" cx="50%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#FFD700" stopOpacity="0.9">
                  <animate attributeName="stop-opacity" values="0.5;0.9;0.5" dur="2s" repeatCount="indefinite" />
                </stop>
                <stop offset="50%" stopColor="#FFA500" stopOpacity="0.5">
                  <animate attributeName="stop-opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor="#FF8C00" stopOpacity="0">
                  <animate attributeName="stop-opacity" values="0;0.2;0" dur="2s" repeatCount="indefinite" />
                </stop>
              </radialGradient>
            </defs>

            {/* Inner glow (light inside) */}
            <ellipse cx="100" cy="85" rx="35" ry="20" fill="url(#innerGlow)" />

            {/* Lampshade body with texture */}
            <path
              d="M 65 70 L 55 130 L 145 130 L 135 70 Z"
              fill="url(#fabricGradient)"
              filter="url(#fabricTexture)"
              stroke="#DAA520"
              strokeWidth="1.5"
              opacity="0.95"
            />

            {/* Top rim */}
            <ellipse cx="100" cy="70" rx="35" ry="8" fill="#DAA520" opacity="0.8" />
            <ellipse cx="100" cy="70" rx="35" ry="8" fill="none" stroke="#B8860B" strokeWidth="1.5" />

            {/* Bottom rim */}
            <ellipse cx="100" cy="130" rx="45" ry="10" fill="#B8860B" opacity="0.7" />
            <ellipse cx="100" cy="130" rx="45" ry="10" fill="none" stroke="#8B6914" strokeWidth="1.5" />

            {/* Decorative stitching pattern */}
            <path d="M 68 80 Q 100 78 132 80" stroke="#8B6914" strokeWidth="0.5" strokeDasharray="2,3" opacity="0.6" fill="none" />
            <path d="M 64 95 Q 100 93 136 95" stroke="#8B6914" strokeWidth="0.5" strokeDasharray="2,3" opacity="0.6" fill="none" />
            <path d="M 60 110 Q 100 108 140 110" stroke="#8B6914" strokeWidth="0.5" strokeDasharray="2,3" opacity="0.6" fill="none" />

            {/* Light bulb inside */}
            <ellipse cx="100" cy="95" rx="12" ry="16" fill="#FFF4E6" opacity="0.8">
              <animate attributeName="opacity" values="0.6;0.95;0.6" dur="2s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="100" cy="95" rx="8" ry="12" fill="#FFFACD">
              <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />
            </ellipse>

            {/* Light rays emanating downward */}
            <g opacity="0.4">
              <line x1="100" y1="130" x2="100" y2="160" stroke="#FFD700" strokeWidth="3" strokeLinecap="round">
                <animate attributeName="opacity" values="0.2;0.6;0.2" dur="2s" repeatCount="indefinite" />
              </line>
              <line x1="85" y1="132" x2="75" y2="155" stroke="#FFD700" strokeWidth="2" strokeLinecap="round">
                <animate attributeName="opacity" values="0.15;0.5;0.15" dur="2s" repeatCount="indefinite" />
              </line>
              <line x1="115" y1="132" x2="125" y2="155" stroke="#FFD700" strokeWidth="2" strokeLinecap="round">
                <animate attributeName="opacity" values="0.15;0.5;0.15" dur="2s" repeatCount="indefinite" />
              </line>
              <line x1="70" y1="135" x2="55" y2="150" stroke="#FFA500" strokeWidth="1.5" strokeLinecap="round">
                <animate attributeName="opacity" values="0.1;0.4;0.1" dur="2s" repeatCount="indefinite" />
              </line>
              <line x1="130" y1="135" x2="145" y2="150" stroke="#FFA500" strokeWidth="1.5" strokeLinecap="round">
                <animate attributeName="opacity" values="0.1;0.4;0.1" dur="2s" repeatCount="indefinite" />
              </line>
            </g>

            {/* Sparkles */}
            <circle cx="75" cy="75" r="2" fill="#FFD700">
              <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="125" cy="85" r="1.5" fill="#FFA500">
              <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="0.5s" />
            </circle>
            <circle cx="90" cy="120" r="2" fill="#FFD700">
              <animate attributeName="opacity" values="0;1;0" dur="1.8s" repeatCount="indefinite" begin="1s" />
            </circle>
          </svg>
        </div>

        {/* Loading text with typewriter effect */}
        <div className="space-y-3">
          <h2 className="text-2xl font-display text-[#F5DEB3] tracking-wide">
            Illumine
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0.15s' }}></div>
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0.3s' }}></div>
            </div>
          </div>
          <p className="text-zinc-400 text-sm font-light tracking-wider">
            Préparation de votre espace créatif
          </p>
        </div>

        {/* Subtle progress hint */}
        <div className="w-48 h-1 mx-auto bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-full animate-pulse"
               style={{
                 width: '60%',
                 animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite, shimmer 2s linear infinite'
               }}>
          </div>
        </div>
      </div>

      {/* Add shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(250%); }
        }
      `}</style>
    </div>
  );
}

function MarketingLeadCapture() {
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);
  const marketingPrefixes = [
    '/pricing',
    '/contact',
    '/about',
    '/faq',
    '/tutoriels',
    '/calculateur-patron-abat-jour',
    '/patron-abat-jour-pdf',
    '/dimensions-abat-jour',
    '/gabarit-abat-jour-sur-mesure',
    '/formes-abat-jour',
    '/formule-patron-abat-jour',
    '/marge-patron-abat-jour',
    '/dessiner-gabarit-abat-jour',
    '/patron-abat-jour-a-imprimer',
    '/patron-abat-jour-conique',
    '/patron-abat-jour-empire',
    '/patron-abat-jour-cylindrique',
    '/patron-abat-jour-tambour',
    '/patron-abat-jour-ovale',
    '/patron-abat-jour-rectangulaire',
    '/patron-abat-jour-hexagonal',
    '/polyphane-abat-jour',
    '/blog',
    '/offre',
    '/checkout',
    '/mentions-legales',
    '/cgu',
  ];

  const isMarketingPage =
    pathname === '/' || marketingPrefixes.some((prefix) => pathname.startsWith(prefix));

  useEffect(() => {
    if (!isMarketingPage) {
      setReady(false);
      return undefined;
    }

    const enable = () => setReady(true);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(enable, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(id);
    }

    const timeout = window.setTimeout(enable, 180);
    return () => window.clearTimeout(timeout);
  }, [isMarketingPage]);

  if (!isMarketingPage || !ready) return null;

  return (
    <Suspense fallback={null}>
      <LeadCaptureWidget />
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AnalyticsInitializer>
        <SwetrixTracker />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Page d'accueil marketing (accessible à tous) */}
                <Route path="/" element={<LandingPage />} />

                {/* Application principale (protégée) */}
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <AppShell />
                    </ProtectedRoute>
                  }
                />

                {/* Dashboard Admin (protégé - admin uniquement) */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />

                {/* Page de partage public (lazy) */}
                <Route path="/share/:token" element={<ShareView />} />

                {/* Pages légales, tarifs et contact (lazy) */}
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/tutoriels" element={<TutorialsPage />} />
                <Route path="/calculateur-patron-abat-jour" element={<LampshadePatternCalculatorPage />} />
                <Route path="/patron-abat-jour-pdf" element={<LampshadePdfPatternGuidePage />} />
                <Route path="/dimensions-abat-jour" element={<LampshadeDimensionsGuidePage />} />
                <Route path="/formes-abat-jour" element={<LampshadeShapeGuidePage />} />
                <Route path="/formule-patron-abat-jour" element={<LampshadeFormulaGuidePage />} />
                <Route path="/marge-patron-abat-jour" element={<LampshadeMarginGuidePage />} />
                <Route path="/dessiner-gabarit-abat-jour" element={<DrawLampshadeTemplateGuidePage />} />
                <Route path="/patron-abat-jour-a-imprimer" element={<PrintableLampshadePatternPage />} />
                <Route path="/patron-abat-jour-conique" element={<ConicalPatternGuidePage />} />
                <Route path="/patron-abat-jour-empire" element={<EmpirePatternGuidePage />} />
                <Route path="/patron-abat-jour-cylindrique" element={<CylindricalPatternGuidePage />} />
                <Route path="/patron-abat-jour-tambour" element={<DrumPatternGuidePage />} />
                <Route path="/patron-abat-jour-ovale" element={<OvalPatternGuidePage />} />
                <Route path="/patron-abat-jour-rectangulaire" element={<RectangularPatternGuidePage />} />
                <Route path="/patron-abat-jour-hexagonal" element={<HexagonalPatternGuidePage />} />
                <Route path="/polyphane-abat-jour" element={<PolyphaneLampshadeGuidePage />} />
                <Route path="/gabarit-abat-jour-sur-mesure" element={<CustomLampshadeTemplatePage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/offre" element={<SalesPage />} />
                <Route path="/checkout" element={<CheckoutFunnel />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/success" element={<SubscriptionSuccessPage />} />

                {/* Pages de test (dev only) */}
                {import.meta.env.DEV && (
                  <>
                    <Route path="/error-test" element={<ErrorTestPage />} />
                    <Route path="/loader-test" element={<LoaderTestPage />} />
                    <Route path="/test-photoreal" element={<PhotorealisticTestPage />} />
                    <Route path="/test-babylon" element={<BabylonTestPage />} />
                    <Route path="/test-babylon-ultra" element={<BabylonUltraPage />} />
                    <Route path="/sliders" element={<SliderShowcase />} />
                  </>
                )}

                {/* Page 404 - doit être en dernier */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
        </Suspense>
        <MarketingLeadCapture />
        <Toaster />
      </AnalyticsInitializer>
    </BrowserRouter>
  );
}
